import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trash2, Mail, Inbox, TrendingDown, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { getRoiLeads, deleteRoiLead } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { toast } from 'sonner';

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };
const fmtCHF = (v) => `CHF ${Math.round(v || 0).toLocaleString('de-CH')}`;
const fmtDate = (iso) => new Date(iso).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

export default function AdminLeadsPage() {
  const { token } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadLeads(); }, []);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const res = await getRoiLeads(token);
      setLeads(res.data);
    } catch (err) { toast.error('Leads konnten nicht geladen werden'); } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteRoiLead(id, token);
      toast.success('Lead gelöscht');
      setLeads(leads.filter(l => l.id !== id));
    } catch (err) { toast.error('Fehler beim Löschen'); }
  };

  const copyEmail = (email) => {
    navigator.clipboard.writeText(email);
    toast.success('E-Mail kopiert');
  };

  return (
    <div className="space-y-6" data-testid="admin-leads-page">
      <PageHeader
        title="ROI-Rechner Leads"
        description={`${leads.length} Interessenten haben ihren PDF-Report angefordert`}
      />

      {loading ? (
        <div className="text-slate-500 text-sm py-12 text-center">Lade Leads…</div>
      ) : leads.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="Noch keine Leads"
          description="Sobald jemand auf dem ROI-Rechner seinen PDF-Report anfordert, erscheint er hier."
        />
      ) : (
        <motion.div initial="hidden" animate="visible" variants={fadeIn} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-500">
                  <th className="px-4 py-3 font-medium">E-Mail</th>
                  <th className="px-4 py-3 font-medium">Datum</th>
                  <th className="px-4 py-3 font-medium">Installs/Monat</th>
                  <th className="px-4 py-3 font-medium">CPI → PPI</th>
                  <th className="px-4 py-3 font-medium">Einsparung/Monat</th>
                  <th className="px-4 py-3 font-medium">Pro Jahr</th>
                  <th className="px-4 py-3 font-medium text-right">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50 transition-colors" data-testid={`lead-row-${lead.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        {lead.email}
                        <button onClick={() => copyEmail(lead.email)} className="text-slate-400 hover:text-teal-600 transition-colors" data-testid={`lead-copy-email-${lead.id}`}>
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{fmtDate(lead.created_at)}</td>
                    <td className="px-4 py-3">{(lead.inputs?.installs || 0).toLocaleString('de-CH')}</td>
                    <td className="px-4 py-3 text-slate-600">CHF {lead.inputs?.cpi?.toFixed(2)} → CHF {lead.inputs?.ppi?.toFixed(2)}</td>
                    <td className="px-4 py-3">
                      <Badge className="bg-teal-50 text-teal-700 hover:bg-teal-50">
                        <TrendingDown className="w-3 h-3 mr-1" /> {fmtCHF(lead.results?.saving_monthly)}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{fmtCHF(lead.results?.saving_yearly)}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <a href={`mailto:${lead.email}?subject=Deine%20ROI-Analyse%20mit%20marKIpilot`}>
                          <Button variant="ghost" size="sm" className="text-teal-600 hover:text-teal-700" data-testid={`lead-contact-${lead.id}`}>
                            <Mail className="w-4 h-4" />
                          </Button>
                        </a>
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleDelete(lead.id)} data-testid={`lead-delete-${lead.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}
    </div>
  );
}
